import { useEffect, useState } from 'react';
import { exportBookshelf, fetchExportColumns } from '../api.js';
import { useToast } from '../Toaster.jsx';

// The file is named here rather than by the server's Content-Disposition,
// which a cross-origin response does not let script read. Today's date in the
// reader's own zone, since it is their download.
function filename() {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `litbyt-books-${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}.csv`;
}

// A fetched file reaches the disk the only way a browser allows: a link to it
// that is clicked. The fetch is needed at all because the session is a cookie
// on the API's origin, and a plain link to the API would leave choosing what
// to do with a refusal to the browser - a page of JSON in a new tab.
function save(blob, name) {
  const href = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = href;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // After the click has been handled, not during it, or some browsers start a
  // download of nothing.
  setTimeout(() => URL.revokeObjectURL(href), 0);
}

export default function ExportPanel() {
  const notify = useToast();
  const [columns, setColumns] = useState(null);
  const [chosen, setChosen] = useState(() => new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchExportColumns()
      .then((list) => {
        if (cancelled) return;
        setColumns(list);
        setChosen(new Set(list.filter((column) => column.default).map((column) => column.id)));
      })
      .catch((error) => {
        if (cancelled) return;
        setColumns([]);
        notify(`Could not load the columns: ${error.message}`, { tone: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [notify]);

  function toggle(id) {
    setChosen((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handleExport(event) {
    event.preventDefault();
    setBusy(true);
    try {
      // In the list's order, which is the file's - the server would put them
      // that way regardless, and asking for them that way keeps the URL honest.
      const ids = columns.filter((column) => chosen.has(column.id)).map((column) => column.id);
      save(await exportBookshelf(ids), filename());
    } catch (error) {
      notify(`Export failed: ${error.message}`, { tone: 'error' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="panel" onSubmit={handleExport}>
      <h2>Export your books</h2>
      <p className="panel-hint">
        Every book on every shelf, as a CSV file a spreadsheet can open. One row a book; pick the
        columns.
      </p>

      {columns === null ? (
        <p className="panel-empty">Loading…</p>
      ) : (
        <fieldset className="export-columns">
          <legend className="drawer-label">Columns</legend>
          {columns.map(({ id, label }) => (
            <label key={id} className="export-column">
              <input
                type="checkbox"
                name={id}
                checked={chosen.has(id)}
                onChange={() => toggle(id)}
              />
              {label}
            </label>
          ))}
        </fieldset>
      )}

      <button type="submit" className="save-button" disabled={busy || chosen.size === 0}>
        {busy ? 'Exporting…' : 'Download CSV'}
      </button>
    </form>
  );
}
